import { useCallback, useEffect, useRef, useState } from 'react';

interface UseVisitedItemsOptions<T> {
  expectedCount: number;
  onAllVisited?: () => void;
  initialVisitedIds?: Iterable<T>;
}

interface UseVisitedItemsResult<T> {
  visitedIds: Set<T>;
  visitedCount: number;
  isComplete: boolean;
  markVisited: (itemId: T) => void;
  resetVisited: () => void;
}

export function useVisitedItems<T>({
  expectedCount,
  onAllVisited,
  initialVisitedIds,
}: UseVisitedItemsOptions<T>): UseVisitedItemsResult<T> {
  const initialVisitedIdsRef = useRef(new Set(initialVisitedIds ?? []));
  const [visitedIds, setVisitedIds] = useState<Set<T>>(
    () => new Set(initialVisitedIdsRef.current)
  );
  const onAllVisitedRef = useRef(onAllVisited);
  const hasNotifiedRef = useRef(false);

  onAllVisitedRef.current = onAllVisited;

  const isComplete =
    expectedCount === 0 ? true : visitedIds.size >= expectedCount;

  useEffect(() => {
    if (expectedCount > 0 && isComplete && !hasNotifiedRef.current) {
      hasNotifiedRef.current = true;
      onAllVisitedRef.current?.();
      return;
    }

    if (!isComplete) {
      hasNotifiedRef.current = false;
    }
  }, [expectedCount, isComplete]);

  const markVisited = useCallback((itemId: T) => {
    setVisitedIds((previous) => {
      if (previous.has(itemId)) {
        return previous;
      }

      const next = new Set(previous);
      next.add(itemId);
      return next;
    });
  }, []);

  const resetVisited = useCallback(() => {
    hasNotifiedRef.current = false;
    setVisitedIds(new Set(initialVisitedIdsRef.current));
  }, []);

  return {
    visitedIds,
    visitedCount: visitedIds.size,
    isComplete,
    markVisited,
    resetVisited,
  };
}
